import { uniqueId } from 'lodash-es';
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useState,
} from 'react';
import useForceUpdate from 'use-force-update';

import ControlledToast, { type ControlledToastProps } from './ControlledToast';
import type { AddToast, RemoveToast, ToastObject } from './types';

export interface ControlledToastHandle {
  addToast: AddToast;
  removeToast: RemoveToast;
}

type ToastProps = Pick<ControlledToastProps, 'transitionTime'>;

const Toast = forwardRef<ControlledToastHandle, ToastProps>(({ transitionTime }, ref) => {
  const [toasts, setToasts] = useState<ToastObject[]>([]);
  const forceUpdate = useForceUpdate();

  const removeToast: RemoveToast = useCallback((id) => {
    setToasts((currentToasts) => currentToasts.filter((toast) => toast.id !== id));
  }, []);

  const addToast: AddToast = useCallback(({ type = 'success', title, message, duration = 5 }) => {
    const id = uniqueId('toast-');

    setToasts((currentToasts) => [{ id, type, title, message, duration }, ...currentToasts]);
  }, []);

  useImperativeHandle(ref, () => ({ addToast, removeToast }));

  useLayoutEffect(() => {
    if (!addToast) {
      return;
    }

    window.addToast = addToast;

    return () => {
      if (window.addToast === addToast) {
        delete window.addToast;
      }
    };
  }, [addToast, forceUpdate]);

  useEffect(() => forceUpdate(), [forceUpdate]);

  return (
    <ControlledToast transitionTime={transitionTime} toasts={toasts} onDismiss={removeToast} />
  );
});

Toast.displayName = 'Toast';

export { ControlledToast };
export type { AddToast, RemoveToast, ToastObject, ToastType } from './types';
export { showToast, toast } from './utils';
export default Toast;
